import React, { useEffect, useRef, useState } from "react";

import {
  CheckOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  LeftOutlined,
  LoadingOutlined,
  MoreOutlined,
  RightOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Checkbox, Input, Modal, Radio, Spin,
} from "antd";
import { toast } from "react-toastify";

import { getFileFromS3WithPresigned, getFilePath, uploadFileToS3WithPresigned } from "../../../utils/helpers";

import {
  createInvoiceHeader,
  deleteInvoiceHeader,
  editInvoiceHeader,
  listInvoiceHeaders,
} from "../../../actions/invoice";

import { ReactComponent as CheckboxCircleLineIcon } from "../../../assets/images/subscriptions/checkbox-circle-line.svg";
import { ReactComponent as DeleteBinLineIcon } from "../../../assets/images/subscriptions/delete-bin-line.svg";
import { ReactComponent as PencilLineIcon } from "../../../assets/images/subscriptions/pencil-line.svg";

import "./InvoiceHeader.css";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/svg+xml"];

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isAcceptedFile = (file) => {
  const name = file.name.toLowerCase();
  return ACCEPTED_TYPES.includes(file.type) || /\.(png|jpe?g|svg)$/.test(name);
};

const emptyUpload = () => ({
  name: "",
  setAsDefault: true,
  file: null,
});

const HEADER_FOLDER = "Invoice/Header/";

// Saved invoice headers are capped at 10. Enforcement is commented out for now.
// Uncomment this constant and the checks in openModal, saveHeader, and the upload
// button to stop users from saving more than 10 headers.
// const MAX_INVOICE_HEADERS = 10;
// const isHeaderLimitReached = (count) => count >= MAX_INVOICE_HEADERS;

const revokePreview = (url) => {
  if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
};

const mapHeader = (header, previewUrl = "") => ({
  id: String(header.header_id),
  name: header.name,
  imageUrl: header.image_url,
  previewUrl,
  isDefault: Boolean(header.is_default),
});

const withPreviewUrls = async (headers, signal) => {
  const keys = headers.map((item) => item.image_url).filter(Boolean);
  let files = null;
  if (keys.length) {
    try {
      files = await getFileFromS3WithPresigned(keys, "private", signal);
    } catch (error) {
      files = null;
    }
    if (!signal?.aborted && keys.some((key) => !files?.[key])) {
      toast.error("Couldn't load header previews");
    }
  }
  return headers.map((item) => {
    const blob = files?.[item.image_url];
    return mapHeader(item, blob ? URL.createObjectURL(blob) : "");
  });
};

export default function InvoiceHeader({ onChange, error = "" }) {
  const fileInputRef = useRef(null);
  const trackRef = useRef(null);
  const [headers, setHeaders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [upload, setUpload] = useState(emptyUpload);
  const [menuId, setMenuId] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [headerToRemove, setHeaderToRemove] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [headerAction, setHeaderAction] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [scrollEdges, setScrollEdges] = useState({ canPrev: false, canNext: false });
  const pendingSelectRef = useRef(null);

  const updateScrollEdges = () => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const canPrev = track.scrollLeft > 1;
    const canNext = track.scrollLeft < maxScroll - 1;
    setScrollEdges((current) => (
      current.canPrev === canPrev && current.canNext === canNext ? current : { canPrev, canNext }
    ));
  };

  const scrollTrack = (direction) => {
    if (trackRef.current) {
      trackRef.current.scrollBy({ left: direction * 296, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    updateScrollEdges();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateScrollEdges);
      return () => window.removeEventListener("resize", updateScrollEdges);
    }
    const observer = new ResizeObserver(updateScrollEdges);
    observer.observe(track);
    return () => observer.disconnect();
  }, [headers, loading]);

  useEffect(() => {
    if (!menuId || headerAction) return undefined;
    const closeMenu = (event) => {
      if (event.target.closest(".InvoiceHeader__Menu, .InvoiceHeader__More")) return;
      setMenuId(null);
    };
    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, [headerAction, menuId]);

  useEffect(() => {
    if (!renameId || headerAction?.type === "rename") return undefined;
    const closeRename = (event) => {
      if (event.target.closest(".InvoiceHeader__Rename")) return;
      setRenameId(null);
      setRenameValue("");
    };
    document.addEventListener("mousedown", closeRename);
    return () => document.removeEventListener("mousedown", closeRename);
  }, [headerAction, renameId]);

  const headersRef = useRef(headers);
  headersRef.current = headers;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const notify = onChangeRef.current;
    if (!notify) return;
    const selected = headers.find((item) => item.id === selectedId) || null;
    notify(selected
      ? {
        id: selected.id,
        name: selected.name,
        imageUrl: selected.imageUrl,
        previewUrl: selected.previewUrl,
      }
      : null);
  }, [headers, selectedId]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const loadHeaders = async () => {
      setLoading(true);
      const response = await listInvoiceHeaders(controller.signal);
      if (!isActive || response.canceled) return;
      if (!response.success) {
        toast.error(response.message || "Failed to load invoice headers");
        setLoading(false);
        return;
      }

      const next = await withPreviewUrls(response.headers, controller.signal);
      if (!isActive) {
        next.forEach((item) => revokePreview(item.previewUrl));
        return;
      }

      setHeaders((current) => {
        current.forEach((item) => revokePreview(item.previewUrl));
        return next;
      });
      setSelectedId((current) => {
        const pending = pendingSelectRef.current;
        pendingSelectRef.current = null;
        if (pending && next.some((item) => item.id === String(pending))) return String(pending);
        if (current && next.some((item) => item.id === current)) return current;
        return next.find((item) => item.isDefault)?.id || null;
      });
      setLoading(false);
    };

    loadHeaders();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [listVersion]);

  useEffect(() => () => {
    headersRef.current.forEach((header) => revokePreview(header.previewUrl));
  }, []);

  const openModal = () => {
    // if (isHeaderLimitReached(headers.length)) {
    //   toast.error("You can save up to 10 invoice headers. Remove one to upload another.");
    //   return;
    // }
    setUpload(emptyUpload());
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    revokePreview(upload.file?.previewUrl);
    setModalOpen(false);
    setUpload(emptyUpload());
  };

  const chooseFile = (file) => {
    if (!file) return;
    if (!isAcceptedFile(file)) {
      toast.error("Upload an SVG, PNG, or JPG file");
      return;
    }
    if (upload.file?.previewUrl) revokePreview(upload.file.previewUrl);
    setUpload((current) => ({
      ...current,
      file: {
        name: file.name,
        sizeLabel: formatFileSize(file.size),
        previewUrl: URL.createObjectURL(file),
        raw: file,
      },
    }));
  };

  const clearFile = () => {
    revokePreview(upload.file?.previewUrl);
    setUpload((current) => ({ ...current, file: null }));
  };

  const hasHeaderName = (name, ignoreId) => headers.some((item) => (
    item.id !== ignoreId && item.name.trim().toLowerCase() === name.trim().toLowerCase()
  ));

  const saveHeader = async () => {
    const headerName = upload.name.trim();
    if (!upload.file?.raw || !headerName || saving) return;
    if (headerName.includes("/")) {
      toast.error("Header name cannot include /");
      return;
    }
    if (hasHeaderName(headerName)) {
      toast.error("A header with this name already exists");
      return;
    }
    // if (isHeaderLimitReached(headers.length)) {
    //   toast.error("You can save up to 10 invoice headers. Remove one to upload another.");
    //   return;
    // }

    setSaving(true);
    const raw = upload.file.raw;
    const nameParts = raw.name.trim().split(".");
    const fileEnding = nameParts.pop();
    const fileName = `${nameParts.join(".")}_${Date.now()}.${fileEnding}`;
    const fileInfo = {
      folderPath: HEADER_FOLDER,
      fileName,
      file: raw,
      type: raw.type,
    };
    const key = getFilePath(fileInfo);
    const uploaded = await uploadFileToS3WithPresigned([fileInfo], "private");
    const imageUrl = uploaded?.[key]?.path;

    if (!imageUrl) {
      toast.error("Failed to upload header");
      setSaving(false);
      return;
    }

    const response = await createInvoiceHeader({
      name: headerName,
      imageUrl,
      isDefault: upload.setAsDefault || headers.length === 0,
    });
    setSaving(false);

    if (!response.success) {
      toast.error(response.message || "Failed to save header");
      return;
    }

    pendingSelectRef.current = response.header?.header_id;
    setListVersion((version) => version + 1);
    revokePreview(upload.file.previewUrl);
    setModalOpen(false);
    setUpload(emptyUpload());
  };

  const openRemoveModal = (id) => {
    setHeaderToRemove(id);
    setMenuId(null);
  };

  const removeHeader = async (id) => {
    if (!id || headerAction) return;
    setHeaderAction({ id, type: "remove" });
    try {
      const response = await deleteInvoiceHeader(id);
      if (!response.success) {
        toast.error(response.message || "Failed to remove header");
        return;
      }
      toast.success("Header removed");

      setHeaders((current) => {
        const removed = current.find((item) => item.id === id);
        revokePreview(removed?.previewUrl);
        return current.filter((item) => item.id !== id);
      });
      setSelectedId((current) => (current === id ? null : current));
      setMenuId(null);
      setHeaderToRemove(null);
    } finally {
      setHeaderAction(null);
    }
  };

  const makeDefault = async (id) => {
    const header = headers.find((item) => item.id === id);
    if (!header || headerAction) return;
    setHeaderAction({ id, type: "default" });
    try {
      const response = await editInvoiceHeader({
        headerId: header.id,
        name: header.name,
        imageUrl: header.imageUrl,
        isDefault: true,
      });
      if (!response.success) {
        toast.error(response.message || "Failed to update header");
        return;
      }
      setHeaders((current) => current.map((item) => ({ ...item, isDefault: item.id === id })));
      setMenuId(null);
    } finally {
      setHeaderAction(null);
    }
  };

  const startRename = (header) => {
    setRenameId(header.id);
    setRenameValue(header.name);
    setMenuId(null);
  };

  const cancelRename = () => {
    setRenameId(null);
    setRenameValue("");
  };

  const toggleSelectedHeader = (id) => {
    setSelectedId((current) => (current === id ? null : id));
  };

  const commitRename = async () => {
    const nextName = renameValue.trim();
    const header = headers.find((item) => item.id === renameId);
    if (!header) return;
    if (!nextName) {
      toast.error("Header name is required");
      return;
    }
    if (nextName === header.name) {
      cancelRename();
      return;
    }
    if (nextName.includes("/")) {
      toast.error("Header name cannot include /");
      return;
    }
    if (hasHeaderName(nextName, header.id)) {
      toast.error("A header with this name already exists");
      return;
    }
    if (headerAction) return;
    setHeaderAction({ id: header.id, type: "rename" });
    try {
      const response = await editInvoiceHeader({
        headerId: header.id,
        name: nextName,
        imageUrl: header.imageUrl,
        isDefault: header.isDefault,
      });
      if (!response.success) {
        toast.error(response.message || "Failed to rename header");
        return;
      }
      setHeaders((current) => current.map((item) => (
        item.id === header.id ? { ...item, name: nextName } : item
      )));
      cancelRename();
    } finally {
      setHeaderAction(null);
    }
  };

  const canUpload = Boolean(upload.name.trim() && upload.file?.raw);

  return (
    <>
      <div className={`InvoiceHeader__Panel${error ? " InvoiceHeader__Panel--error" : ""}`}>
        <div className="InvoiceHeader__PanelHeader">
          <div>
            <h3>Invoice header</h3>
            <p>Choose a saved header for this invoice or upload a new one.</p>
            {/* Uncomment with MAX_INVOICE_HEADERS to explain the 10-header cap.
            {isHeaderLimitReached(headers.length) && (
              <p>You can save up to 10 invoice headers. Remove one to upload another.</p>
            )}
            */}
            {error && <span className="InvoiceHeader__Error" role="alert">{error}</span>}
          </div>
          {headers.length > 0 && (
            <button
              type="button"
              className="InvoiceHeader__UploadLink"
              // disabled={isHeaderLimitReached(headers.length)}
              onClick={openModal}
            >
              <CloudUploadOutlined aria-hidden />
              Upload header
            </button>
          )}
        </div>

        {loading && (
          <div className="InvoiceHeader__Empty InvoiceHeader__Empty--loading">
            <Spin />
            <p className="InvoiceHeader__EmptyText">Loading headers...</p>
          </div>
        )}

        {!loading && headers.length === 0 && (
          <div className="InvoiceHeader__Empty">
            <p className="InvoiceHeader__EmptyTitle">No invoice headers yet</p>
            <p className="InvoiceHeader__EmptyText">Upload a header to personalise your invoices.</p>
            <button type="button" className="InvoiceHeader__UploadLink" onClick={openModal}>
              <CloudUploadOutlined aria-hidden />
              Upload header
            </button>
          </div>
        )}

        {!loading && headers.length > 0 && (
          <div
            className={[
              "InvoiceHeader__Carousel",
              scrollEdges.canPrev ? "InvoiceHeader__Carousel--fadeStart" : "",
              scrollEdges.canNext ? "InvoiceHeader__Carousel--fadeEnd" : "",
            ].filter(Boolean).join(" ")}
          >
            <div
              className={`InvoiceHeader__Track${renameId ? " InvoiceHeader__Track--renaming" : ""}`}
              ref={trackRef}
              onScroll={updateScrollEdges}
            >
              {headers.map((header) => {
                const isSelected = header.id === selectedId;
                const isRenaming = header.id === renameId;

                return (
                  <article
                    key={header.id}
                    className={`InvoiceHeader__Card${isSelected ? " is-selected" : ""}`}
                  >
                    <Radio
                      className="InvoiceHeader__Radio"
                      checked={isSelected}
                      aria-label={`${isSelected ? "Unselect" : "Select"} ${header.name}`}
                      onClick={() => toggleSelectedHeader(header.id)}
                    />
                    <button
                      type="button"
                      className="InvoiceHeader__More"
                      aria-label={`Actions for ${header.name}`}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={() => setMenuId((current) => (current === header.id ? null : header.id))}
                    >
                      <MoreOutlined />
                    </button>
                    <button
                      type="button"
                      className="InvoiceHeader__Preview"
                      onClick={() => toggleSelectedHeader(header.id)}
                    >
                      {header.previewUrl && <img src={header.previewUrl} alt="" />}
                    </button>
                    {isRenaming ? (
                      <div className="InvoiceHeader__Rename">
                        <Input
                          autoFocus
                          value={renameValue}
                          onChange={(event) => setRenameValue(event.target.value)}
                          onPressEnter={commitRename}
                          onKeyDown={(event) => {
                            if (event.key === "Escape") cancelRename();
                          }}
                        />
                        <div className="InvoiceHeader__RenameActions">
                          <button
                            type="button"
                            className="InvoiceHeader__RenameCancel"
                            aria-label="Cancel rename"
                            disabled={headerAction?.type === "rename"}
                            onClick={cancelRename}
                          >
                            <CloseOutlined />
                          </button>
                          <button
                            type="button"
                            className="InvoiceHeader__RenameSave"
                            aria-label="Save name"
                            disabled={headerAction?.type === "rename"}
                            onClick={commitRename}
                          >
                            {headerAction?.type === "rename" ? <LoadingOutlined /> : <CheckOutlined />}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="InvoiceHeader__Meta">
                        <span className="InvoiceHeader__Name">{header.name}</span>
                        {header.isDefault && <span className="InvoiceHeader__Badge">Default</span>}
                      </div>
                    )}
                    {menuId === header.id && (
                      <div
                        className="InvoiceHeader__Menu"
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          disabled={Boolean(headerAction)}
                          onClick={() => makeDefault(header.id)}
                        >
                          {headerAction?.id === header.id && headerAction?.type === "default" ? (
                            <LoadingOutlined />
                          ) : (
                            <CheckboxCircleLineIcon aria-hidden />
                          )}
                          Set as default
                        </button>
                        <button
                          type="button"
                          disabled={Boolean(headerAction)}
                          onClick={() => startRename(header)}
                        >
                          <PencilLineIcon aria-hidden />
                          Rename
                        </button>
                        <button
                          type="button"
                          className="InvoiceHeader__MenuRemove"
                          disabled={Boolean(headerAction)}
                          onClick={() => openRemoveModal(header.id)}
                        >
                          <DeleteBinLineIcon aria-hidden />
                          Remove
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
            {scrollEdges.canPrev && (
              <button
                type="button"
                className="InvoiceHeader__Nav InvoiceHeader__Nav--prev"
                aria-label="Show previous headers"
                onClick={() => scrollTrack(-1)}
              >
                <LeftOutlined />
              </button>
            )}
            {scrollEdges.canNext && (
              <button
                type="button"
                className="InvoiceHeader__Nav InvoiceHeader__Nav--next"
                aria-label="Show more headers"
                onClick={() => scrollTrack(1)}
              >
                <RightOutlined />
              </button>
            )}
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        title={null}
        footer={null}
        closable={false}
        width={480}
        centered
        destroyOnHidden
        onCancel={closeModal}
        className="InvoiceHeaderModal"
      >
        <div className="InvoiceHeaderModal__Header">
          <h3>Upload invoice header</h3>
          <button type="button" aria-label="Close" onClick={closeModal}>
            <CloseOutlined />
          </button>
        </div>
        <div className="InvoiceHeaderModal__Body">
          <label className="InvoiceHeaderModal__Field">
            <span>Header name</span>
            <Input
              value={upload.name}
              placeholder="Name your header"
              onChange={(event) => setUpload((current) => ({ ...current, name: event.target.value }))}
            />
          </label>
          <div className="InvoiceHeaderModal__Field">
            <span>Upload File</span>
            <button
              type="button"
              className="InvoiceHeaderModal__Drop"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                chooseFile(event.dataTransfer.files?.[0]);
              }}
            >
              <CloudUploadOutlined aria-hidden />
              <p>
                <span>Click to upload</span>
                {" or drag and drop"}
              </p>
              <small>SVG, PNG or JPG (Recommended: 1785x270px)</small>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
              hidden
              onChange={(event) => {
                chooseFile(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            {upload.file && (
              <div className="InvoiceHeaderModal__File">
                <span className="InvoiceHeaderModal__FileIcon" aria-hidden>
                  <UploadOutlined />
                </span>
                <div>
                  <strong>{upload.file.name}</strong>
                  <p>{upload.file.sizeLabel}</p>
                </div>
                <button type="button" aria-label="Remove file" onClick={clearFile}>
                  <DeleteOutlined />
                </button>
              </div>
            )}
          </div>
          <div className="InvoiceHeaderModal__Default">
            <Checkbox
              checked={upload.setAsDefault}
              onChange={(event) => setUpload((current) => ({
                ...current,
                setAsDefault: event.target.checked,
              }))}
            />
            <span>
              <strong>Set as default header</strong>
              <small>Preselected for new invoices. You can clear it and continue with no header.</small>
            </span>
          </div>
        </div>
        <div className="InvoiceHeaderModal__Footer">
          <button type="button" className="InvoiceHeaderModal__Cancel" onClick={closeModal}>
            Cancel
          </button>
          <button
            type="button"
            className="InvoiceHeaderModal__Submit"
            disabled={!canUpload || saving}
            onClick={saveHeader}
          >
            {saving && <LoadingOutlined />}
            {saving ? "Uploading" : "Upload"}
          </button>
        </div>
      </Modal>

      <Modal
        open={Boolean(headerToRemove)}
        title={null}
        footer={null}
        closable={false}
        width={400}
        centered
        onCancel={() => {
          if (headerAction?.type === "remove") return;
          setHeaderToRemove(null);
        }}
        className="InvoiceHeaderRemove"
      >
        <div className="InvoiceHeaderRemove__Body">
          <span className="InvoiceHeaderRemove__Icon" aria-hidden>
            <DeleteOutlined />
          </span>
          <h3>Remove this header?</h3>
          <p>
            This header will be removed from your saved headers and will no longer be available for future invoices.
          </p>
          <div className="InvoiceHeaderRemove__Actions">
            <button
              type="button"
              disabled={headerAction?.type === "remove"}
              onClick={() => setHeaderToRemove(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="is-remove"
              disabled={headerAction?.type === "remove"}
              onClick={() => removeHeader(headerToRemove)}
            >
              {headerAction?.type === "remove" && <LoadingOutlined />}
              {headerAction?.type === "remove" ? "Removing" : "Remove"}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
